import type { TDocumentDefinitions, Content, TableCell } from 'pdfmake/interfaces';
import {
  mm, fmtPrice, fmtDate, interpolate,
  companyLines, companySocialsLine, partyLines, logoBlock, totalsRows, buildVars,
  computeVatBreakdown,
  COLUMN_LABELS, COLUMN_WIDTHS, linesCell,
  type DesignCtx,
} from './design-utils';
import { amountInWordsFr } from '../amount-in-words';

// "Classic" — boxy, colored title, grey borders. Closest to the previous
// hardcoded look. Good default for Tunisian SMB invoices.
export function buildClassicDoc(ctx: DesignCtx): TDocumentDefinitions {
  const { options: o, data, company, docTypeTitle } = ctx;
  const vars = buildVars(ctx);

  const title = o.customTitle || docTypeTitle;
  const partyLabel = data.supplier ? 'FOURNISSEUR' : 'CLIENT';
  const partyTxt = partyLines(data.supplier ?? data.client, o);

  // Header: logo on one side, title + number on the other; company info below.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const headerCols: any[] = [];
  const logo = logoBlock(ctx);
  const socialsLine = companySocialsLine(company, o);
  const companyStack: { stack: Content[] } = {
    stack: [
      ...(o.showCompanyName ? [{ text: company.companyName, fontSize: 16, bold: true, color: o.primaryColor } as Content] : []),
      ...companyLines(company, o).map((line) => ({ text: line, fontSize: 9, color: '#555' } as Content)),
      ...(socialsLine ? [{ text: socialsLine, fontSize: 8, color: '#777', margin: [0, 2, 0, 0] } as Content] : []),
    ],
  };
  const titleStack: { stack: Content[]; alignment: 'right' } = {
    stack: [
      ...(o.showTitle ? [{ text: title, fontSize: 18, bold: true, color: o.primaryColor } as Content] : []),
      ...(o.showNumber ? [{ text: data.number, fontSize: 11, color: '#666' } as Content] : []),
    ],
    alignment: 'right',
  };

  if (o.logoPosition === 'left' && logo) {
    headerCols.push({ width: mm(o.logoSizeMm + 5), stack: [logo] });
    headerCols.push({ width: '*', ...companyStack });
    headerCols.push({ width: 'auto', ...titleStack });
  } else if (o.logoPosition === 'right' && logo) {
    headerCols.push({ width: '*', ...companyStack });
    headerCols.push({ width: 'auto', ...titleStack });
    headerCols.push({ width: mm(o.logoSizeMm + 5), stack: [logo], alignment: 'right' });
  } else if (o.logoPosition === 'center' && logo) {
    headerCols.push({ width: '*', ...companyStack });
    headerCols.push({ width: mm(o.logoSizeMm + 5), stack: [logo], alignment: 'center' });
    headerCols.push({ width: 'auto', ...titleStack });
  } else {
    headerCols.push({ width: '*', ...companyStack });
    headerCols.push({ width: 'auto', ...titleStack });
  }

  const infoRow: Content = {
    margin: [0, 15, 0, 10],
    columns: [
      {
        width: '*',
        stack: [
          { text: 'INFORMATIONS', fontSize: 9, bold: true, color: '#999', margin: [0, 0, 0, 3] },
          ...(o.showDate ? [{ text: `Date : ${fmtDate(data.docDate)}`, fontSize: 9 } as Content] : []),
          ...(o.showValidityDate && data.validityDate ? [{ text: `Validité : ${fmtDate(data.validityDate)}`, fontSize: 9 } as Content] : []),
        ],
      },
      {
        width: '*',
        stack: [
          { text: partyLabel, fontSize: 9, bold: true, color: '#999', margin: [0, 0, 0, 3] },
          ...partyTxt.map((line, i) => ({ text: line, fontSize: i === 0 ? 11 : 9, bold: i === 0 })),
        ],
      },
    ],
  };

  const header: TableCell[] = o.linesColumns.map((c) => ({
    text: COLUMN_LABELS[c],
    bold: true,
    fontSize: 9,
    alignment: c === 'description' ? 'left' : c === 'number' ? 'center' : 'right',
  }));
  const rows: TableCell[][] = data.lines.map((l) => o.linesColumns.map((c) => linesCell(c, l)));

  const linesTable: Content = {
    margin: [0, 0, 0, 15],
    table: {
      headerRows: 1,
      widths: o.linesColumns.map((c) => COLUMN_WIDTHS[c]),
      body: [header, ...rows],
    },
    layout: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      hLineWidth: (i: number, node: any) => (i === 0 || i === 1 || i === node.table.body.length) ? 1 : 0.5,
      vLineWidth: () => 0,
      hLineColor: (i: number) => (i <= 1 ? '#333' : '#DDD'),
      paddingLeft: () => 6, paddingRight: () => 6, paddingTop: () => 4, paddingBottom: () => 4,
      fillColor: (i: number) => i === 0 ? '#F5F5F5' : (o.zebraRows && i > 0 && i % 2 === 0) ? '#FAFAFA' : null,
    },
  };

  // VAT breakdown (grouped by rate) — side-by-side with the totals table
  const vatRows = o.showVatBreakdown ? computeVatBreakdown(data.lines) : [];
  const vatHeader: TableCell[] = [
    { text: 'Base TVA', fontSize: 9, bold: true, fillColor: '#F5F5F5', alignment: 'left' },
    { text: 'Taux TVA', fontSize: 9, bold: true, fillColor: '#F5F5F5', alignment: 'center' },
    { text: 'TVA', fontSize: 9, bold: true, fillColor: '#F5F5F5', alignment: 'right' },
  ];
  const vatBodyRows: TableCell[][] = vatRows.map((r) => [
    { text: fmtPrice(r.base), alignment: 'left', fontSize: 9 },
    { text: `${r.rate}%`, alignment: 'center', fontSize: 9 },
    { text: fmtPrice(r.tax), alignment: 'right', fontSize: 9 },
  ]);
  const vatBreakdownTable: Content = (o.showVatBreakdown && vatRows.length > 0) ? {
    table: { widths: ['*', 'auto', 'auto'], body: [vatHeader, ...vatBodyRows] },
    layout: {
      hLineWidth: () => 0.5, vLineWidth: () => 0.5,
      hLineColor: () => '#CCC', vLineColor: () => '#CCC',
      paddingLeft: () => 5, paddingRight: () => 5, paddingTop: () => 3, paddingBottom: () => 3,
    },
  } : { text: '' };

  const totals = totalsRows(ctx);
  const totalsBodyTable: Content = totals.length ? {
    table: { widths: ['*', 'auto'], body: totals },
    layout: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? 0.5 : 0,
      vLineWidth: () => 0.5,
      hLineColor: () => '#CCC', vLineColor: () => '#CCC',
      paddingTop: () => 3, paddingBottom: () => 3, paddingLeft: () => 5, paddingRight: () => 5,
      fillColor: (i: number, node: { table: { body: TableCell[][] } }) =>
        // highlight the label column of the totals — matches the reference BL
        node.table.body[i]?.[0] && (i % 2 === 0) ? null : null,
    },
  } : { text: '' };

  const totalsTable: Content = (totals.length || vatRows.length) ? {
    margin: [0, 0, 0, 12],
    columns: [
      vatRows.length > 0 ? { width: mm(85), stack: [vatBreakdownTable] } : { width: '*', text: '' },
      { width: 10, text: '' },
      totals.length > 0 ? { width: mm(85), stack: [totalsBodyTable] } : { width: '*', text: '' },
    ],
  } : { text: '' };

  // Amount in words — "Arrêter le Présent [doc] à la Somme de: ..."
  const amountLabel = (() => {
    const docLabel = (o.customTitle || docTypeTitle).toLowerCase();
    if (docLabel.includes('devis')) return 'Arrêter le Présent Devis à la Somme de :';
    if (docLabel.includes('livraison')) return 'Arrêter le Présent BL à la Somme de :';
    if (docLabel.includes('avoir')) return 'Arrêter le Présent Avoir à la Somme de :';
    if (docLabel.includes('réception') || docLabel.includes('reception')) return 'Arrêter le Présent BR à la Somme de :';
    if (docLabel.includes('commande')) return 'Arrêter la Présente Commande à la Somme de :';
    return 'Arrêter la Présente Facture à la Somme de :';
  })();
  const amountInWordsBlock: Content = o.showAmountInWords ? {
    stack: [
      { text: amountLabel, fontSize: 9, margin: [0, 0, 0, 2] },
      { text: amountInWordsFr(data.totalTtc), fontSize: 9, bold: true },
    ],
    margin: [0, 0, 0, 15],
  } : { text: '' };

  const notesBlock: Content = (o.showNotes && data.notes) ? {
    stack: [
      { text: 'Notes / Conditions', fontSize: 9, bold: true, color: '#999', margin: [0, 0, 0, 4] },
      { text: data.notes, fontSize: 9, color: '#333' },
    ],
    margin: [0, 0, 0, 15],
  } : { text: '' };

  return {
    pageSize: 'A4',
    pageMargins: [mm(15), mm(15), mm(15), mm(20)],
    content: [
      { columns: headerCols },
      infoRow,
      linesTable,
      totalsTable,
      amountInWordsBlock,
      notesBlock,
    ],
    footer: o.showFooter ? ((currentPage: number, pageCount: number): Content => ({
      margin: [mm(15), 6, mm(15), 0],
      columns: [
        { text: interpolate(o.footerText, vars), fontSize: 8, color: '#999', alignment: o.showPageNumbers ? 'left' : 'center' },
        ...(o.showPageNumbers ? [{ text: `${currentPage} / ${pageCount}`, fontSize: 8, color: '#999', alignment: 'right' as const }] : []),
      ],
    })) : undefined,
    defaultStyle: { fontSize: 9 },
  };
}
